import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Plus,
  FileText,
  PenTool,
  Puzzle,
  Layout,
  Copy,
  Wand2,
  ClipboardList,
} from "lucide-react";
import { TodayBlock } from "./TodayBlock";
import { LessonPlanEditor } from "./LessonPlanEditor";

const CREATION_OPTIONS = [
  {
    id: "fiche-prep",
    title: "Fiche de prép",
    description: "Préparer une séance structurée",
    icon: ClipboardList,
    color: "bg-indigo-500",
    lightColor: "bg-indigo-50 hover:bg-indigo-100 border-indigo-200",
    isDialog: true,
  },
  {
    id: "text",
    title: "Nouveau texte",
    description: "Créer un texte adapté",
    icon: FileText,
    color: "bg-blue-500",
    lightColor: "bg-blue-50 hover:bg-blue-100 border-blue-200",
    action: "editor",
  },
  {
    id: "fiche",
    title: "Nouvelle fiche",
    description: "Fiche d'exercices visuelle",
    icon: Layout,
    color: "bg-purple-500",
    lightColor: "bg-purple-50 hover:bg-purple-100 border-purple-200",
    action: "create",
  },
  {
    id: "exercice",
    title: "Nouvel exercice",
    description: "Exercice interactif",
    icon: Puzzle,
    color: "bg-green-500",
    lightColor: "bg-green-50 hover:bg-green-100 border-green-200",
    action: "create?template=exercice",
  },
  {
    id: "editeur",
    title: "Éditeur visuel",
    description: "Création libre drag & drop",
    icon: PenTool,
    color: "bg-orange-500",
    lightColor: "bg-orange-50 hover:bg-orange-100 border-orange-200",
    action: "create",
  },
  {
    id: "modele",
    title: "Depuis un modèle",
    description: "Choisir un template",
    icon: Copy,
    color: "bg-pink-500",
    lightColor: "bg-pink-50 hover:bg-pink-100 border-pink-200",
    action: "create?showTemplates=true",
  },
  {
    id: "ai",
    title: "Généré par IA",
    description: "Assistant Navi",
    icon: Wand2,
    color: "bg-rose-500",
    lightColor: "bg-rose-50 hover:bg-rose-100 border-rose-200",
    action: "create?showAI=true",
  },
];

export function TeacherCreationTab() {
  const navigate = useNavigate();
  const [showLessonPlanEditor, setShowLessonPlanEditor] = useState(false);

  const handleAction = (option) => {
    if (option.isDialog && option.id === "fiche-prep") {
      setShowLessonPlanEditor(true);
    } else if (option.action) {
      navigate(`/${option.action}`);
    }
  };

  const handleLessonPlanSave = (plan) => {
    setShowLessonPlanEditor(false);
    // Could navigate to the plan view or show a success message
  };

  return (
    <div className="space-y-6">
      {/* Today Block - shows today's sessions with alerts */}
      <TodayBlock />

      <div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Création de contenu</h2>
        <p className="text-slate-500 text-sm">
          Choisissez le type de ressource que vous souhaitez créer
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CREATION_OPTIONS.map((option) => (
          <Card
            key={option.id}
            data-testid={`creation-${option.id}`}
            className={`cursor-pointer transition-all duration-200 border-2 ${option.lightColor}`}
            onClick={() => handleAction(option)}
          >
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className={`${option.color} p-3 rounded-xl`}>
                  <option.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-800 mb-1">{option.title}</h3>
                  <p className="text-sm text-slate-500">{option.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick action button for mobile */}
      <div className="md:hidden fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => navigate("/create")}
          className="w-14 h-14 rounded-full bg-indigo-600 hover:bg-indigo-700 shadow-lg"
        >
          <Plus className="w-6 h-6" />
        </Button>
      </div>

      {/* Lesson Plan Editor Dialog */}
      <Dialog open={showLessonPlanEditor} onOpenChange={setShowLessonPlanEditor}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <LessonPlanEditor 
            onSave={handleLessonPlanSave}
            onClose={() => setShowLessonPlanEditor(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
